import Alpine from 'alpinejs';
import { db, getOrCreateSettings } from './db/db.js';
import {
  quickAdd,
  consumeBatchAmount,
  consumeFifo,
  markBatchOpened,
  discardBatch,
  discardItem,
  setBatchQuantity,
  setItemMinQuantity,
  updateBatch,
} from './domain/item.js';
import {
  loadInventoryView,
  type InventoryView,
  type ItemRow,
  type LocationGroup,
} from './domain/inventory-view.js';
import { seedKnowledgeIfEmpty } from './domain/knowledge.js';
import { mergeDuplicateActiveItems } from './domain/migrations.js';
import {
  aggregateItemStatus,
  getExpiryStatus,
  type ExpiryInfo,
  type ExpiryStatus,
} from './domain/expiry.js';
import type { Batch, Item, PantryKnowledge } from './db/schema.js';
import { getPlatform } from './platform/index.js';
import { initI18n, tLocation } from './i18n/index.js';
import { exportBackupBlob, importBackupFromText } from './domain/backup.js';
import { updateSettings } from './domain/settings.js';
import {
  addShoppingItem,
  cancelShoppingItem,
  loadShoppingDatalistOptions,
  lookupLastUnitPrice,
  markInTransit,
  markReceived,
  type LastUnitPrice,
  type ShoppingDatalistOptions,
} from './domain/shopping.js';
import { lookupKnowledge } from './domain/knowledge.js';
import {
  consumeStep,
  normalizeUnitPrice,
  priceDeltaPct,
  type NormalizedPrice,
} from './domain/normalize-price.js';
import { classifyUsagePattern } from './domain/usage-pattern.js';
import {
  loadShoppingView,
  type ShoppingView,
} from './domain/shopping-view.js';
import {
  loadSubscriptionView,
  type SubscriptionRow,
  type SubscriptionView,
} from './domain/subscription-view.js';
import {
  advanceSubscription,
  createSubscription,
  deactivateSubscription,
  updateSubscription,
  type CreateSubscriptionInput,
  type UpdateSubscriptionInput,
} from './domain/subscription.js';
import type { SubscriptionFrequency } from './events/types.js';
import {
  loadSuggestions,
  type Suggestion,
} from './domain/suggestions.js';
import type { OrderSource } from './events/types.js';

type Tab = 'today' | 'inventory' | 'shopping' | 'settings';

const APP_VERSION = '0.1.0-dev';
const BUILD_TIME = __BUILD_TIME__;
const COMMIT_HASH = __COMMIT_HASH__;

/** Build time 顯示用：「2026-05-01 14:32 UTC」格式（避免時區混淆） */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

// 11 個 category PNG 透過 Vite import.meta.glob 預先 bundle（含內容雜湊），
// runtime 用 category key 查 URL。
const CATEGORY_ICON_URLS = import.meta.glob<string>(
  '../assets/categories/category-*.png',
  { eager: true, import: 'default', query: '?url' },
);

// v0.5.4 主題切換（spec 0.5.3 §2.3）：
// - 'light' / 'dark' 強制覆寫；'system' 跟 OS prefers-color-scheme
// - 把 effective theme 寫進 document.documentElement.dataset.theme，CSS 走 :root[data-theme="dark"] 套用
// - meta theme-color 隨主題切換（影響 iOS Safari status bar / Android browser chrome 配色）
function resolveEffectiveTheme(mode: 'system' | 'light' | 'dark'): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(mode: 'system' | 'light' | 'dark'): void {
  const effective = resolveEffectiveTheme(mode);
  document.documentElement.dataset.theme = effective;
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', effective === 'dark' ? '#1A1410' : '#6B4220');
  }
}

let systemThemeListenerSetup = false;
function setupSystemThemeListener(onChange: () => void): void {
  if (systemThemeListenerSetup) return;
  systemThemeListenerSetup = true;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  // addEventListener 較新；老瀏覽器 fallback addListener
  const listener = () => onChange();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', listener);
  } else if (typeof (mq as MediaQueryList & { addListener?: (cb: () => void) => void }).addListener === 'function') {
    (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(listener);
  }
}

interface QuickAddForm {
  open: boolean;
  name: string;
  quantity: number;
  submitting: boolean;
  error: string | null;
}

interface DetailViewBatch extends Batch {
  expiry: ExpiryInfo;
}

interface DetailView {
  open: boolean;
  item: Item | null;
  batches: DetailViewBatch[];
  expiry: ExpiryInfo;
  totalQuantity: number;
}

interface SettingsView {
  exportBusy: boolean;
  importBusy: boolean;
  importMessage: string | null;
  importError: string | null;
}

type ThemeMode = 'system' | 'light' | 'dark';

interface ShoppingAddForm {
  open: boolean;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number | null;
  packageSize: number | null;
  packageContentUnit: string;
  /** v0.3 chip preset：跟 batch edit dialog 同邏輯 */
  presetSizes: number[];
  presetSizeUnit: string;
  showCustomPackaging: boolean;
  submitting: boolean;
  error: string | null;
  lastPriceHint: LastUnitPrice | null;
  hintLoading: boolean;
}

interface ShoppingContext {
  source: OrderSource | '';
  sourceName: string;
}

interface ShoppingContextDialog {
  open: boolean;
  source: OrderSource | '';
  sourceName: string;
}

interface LocationAddDialog {
  open: boolean;
  name: string;
  submitting: boolean;
  error: string | null;
}

export interface LocationCard {
  location: string;
  count: number;
}

interface ReceiveDialog {
  open: boolean;
  shoppingItemId: string | null;
  name: string;
  unitPrice: number | null;
  defaultUnitPrice: number | null;
  packageSize: number | null;
  packageContentUnit: string;
  sourceName: string;
  submitting: boolean;
}

interface InTransitDialog {
  open: boolean;
  shoppingItemId: string | null;
  name: string;
  /** YYYY-MM-DD（datepicker input 格式） */
  expectedArrival: string;
  submitting: boolean;
}

interface SubscriptionDialog {
  open: boolean;
  /** null = 新增；string = 編輯該 sub.id（v0.4.1 暫不做編輯，留 future） */
  editingId: string | null;
  defaultName: string;
  knowledgeId: string;
  defaultQuantity: number;
  defaultUnit: string;
  source: string;
  frequency: SubscriptionFrequency;
  customDays: number;
  /** YYYY-MM-DD */
  nextDelivery: string;
  leadDays: number;
  submitting: boolean;
  error: string | null;
}

interface BatchEditDialog {
  open: boolean;
  itemId: string | null;
  batchId: string | null;
  quantity: number;
  unit: string;
  packageSize: number | null;
  packageUnit: string;
  expiresAt: string; // YYYY-MM-DD or ''
  /** v0.3：從 PantryKnowledge.commonPackageSizes 帶來的快選 chip 列表。 */
  presetSizes: number[];
  /** 對應 compareBaseUnit（chip 顯示時用）。 */
  presetSizeUnit: string;
  /** 點「自訂」才展開下方手填 input row。 */
  showCustomPackaging: boolean;
  submitting: boolean;
}

const SOURCE_NAME_DEFAULTS = [
  '家樂福', '全聯', 'Costco', '頂好', '楓康', '全家', '7-11', 'Welcome', '美廉社',
  '東門市場', '濱江市場', '南門市場', '主婦聯盟',
];

const PACKAGE_CONTENT_UNIT_DEFAULTS = [
  'ml', 'g', 'kg', 'L', '顆', '個', '入', '抽', '卷', '片', '把', '條', '根', '包', '盒',
];

const QUANTITY_UNIT_DEFAULTS = [
  '個', '顆', '包', '罐', '瓶', '盒', '條', '把', '片', '入', '袋', '串', '盤', '塊',
];

interface AppShellState {
  ready: boolean;
  version: string;
  buildTime: string;
  commitHash: string;
  platformName: string;
  currentTab: Tab;
  view: InventoryView;
  shopping: ShoppingView;
  subscriptions: SubscriptionView;
  suggestions: Suggestion[];
  knownLocations: string[];
  modal: QuickAddForm;
  shoppingModal: ShoppingAddForm;
  selectedLocation: string | null;
  locationAddDialog: LocationAddDialog;
  receiveDialog: ReceiveDialog;
  inTransitDialog: InTransitDialog;
  subscriptionDialog: SubscriptionDialog;
  shoppingContext: ShoppingContext;
  shoppingContextDialog: ShoppingContextDialog;
  detail: DetailView;
  batchEdit: BatchEditDialog;
  minQuantityDraft: number;
  settings: SettingsView;
  datalistOptions: ShoppingDatalistOptions;
  pantryKnowledgeOptions: PantryKnowledge[];
  toast: string | null;
  warningDays: number;
  /** v0.5.4 主題模式（spec 0.5.3 dark mode；'system' 跟 OS appearance）*/
  theme: ThemeMode;
  /** v0.5.5 通知啟用（既有 schema 欄位 wire 進 UI）*/
  notificationsEnabled: boolean;
  init(this: AppShellState): Promise<void>;
  refresh(this: AppShellState): Promise<void>;
  switchTab(this: AppShellState, tab: Tab): void;
  setTheme(this: AppShellState, value: ThemeMode): Promise<void>;
  setNotificationsEnabled(this: AppShellState, value: boolean): Promise<void>;
  setExpiryWarningDays(this: AppShellState, value: number): Promise<void>;
  selectLocation(this: AppShellState, location: string | null): void;
  visibleLocationGroups(this: AppShellState): LocationGroup[];
  locationCards(this: AppShellState): LocationCard[];
  openLocationAdd(this: AppShellState): void;
  closeLocationAdd(this: AppShellState): void;
  submitLocationAdd(this: AppShellState): Promise<void>;
  deleteSelectedLocation(this: AppShellState): Promise<void>;
  selectedLocationItemCount(this: AppShellState): number;
  openQuickAdd(this: AppShellState): void;
  closeQuickAdd(this: AppShellState): void;
  submitQuickAdd(this: AppShellState): Promise<void>;
  openDetail(this: AppShellState, itemId: string): Promise<void>;
  closeDetail(this: AppShellState): void;
  reloadDetail(this: AppShellState): Promise<void>;
  consumeOne(this: AppShellState): Promise<void>;
  consumeOneFullPackaging(this: AppShellState): Promise<void>;
  detailHasOnlyContinuousOpened(this: AppShellState): boolean;
  wipeInventoryData(this: AppShellState): Promise<void>;
  consumeBatchOne(this: AppShellState, batchId: string): Promise<void>;
  consumeBatchAll(this: AppShellState, batchId: string): Promise<void>;
  openBatch(this: AppShellState, batchId: string): Promise<void>;
  discardThisBatch(this: AppShellState, batchId: string): Promise<void>;
  discardThisItem(this: AppShellState): Promise<void>;
  exportBackup(this: AppShellState): Promise<void>;
  importBackup(this: AppShellState, evt: Event): Promise<void>;
  openShoppingAdd(this: AppShellState): void;
  closeShoppingAdd(this: AppShellState): void;
  submitShoppingAdd(this: AppShellState): Promise<void>;
  openShoppingContextEdit(this: AppShellState): void;
  closeShoppingContextEdit(this: AppShellState): void;
  saveShoppingContext(this: AppShellState): Promise<void>;
  clearShoppingContext(this: AppShellState): Promise<void>;
  shoppingContextLabel(this: AppShellState): string;
  shoppingContextMatchesRow(
    this: AppShellState,
    row: { source?: string; sourceName?: string },
  ): boolean;
  contextMatchedPlannedCount(this: AppShellState): number;
  contextMatchedPlannedTotal(this: AppShellState): number;
  markAllInContextReceived(this: AppShellState): Promise<void>;
  onShoppingNameInput(this: AppShellState): void;
  applyLastPriceHint(this: AppShellState): void;
  applyShoppingSizePreset(this: AppShellState, size: number): void;
  openReceiveDialog(this: AppShellState, id: string): Promise<void>;
  closeReceiveDialog(this: AppShellState): void;
  submitReceive(this: AppShellState): Promise<void>;
  receiveShopping(this: AppShellState, id: string): Promise<void>;
  openInTransitDialog(this: AppShellState, id: string): Promise<void>;
  closeInTransitDialog(this: AppShellState): void;
  submitInTransit(this: AppShellState): Promise<void>;
  openSubscriptionAdd(this: AppShellState): void;
  openSubscriptionEdit(this: AppShellState, sub: SubscriptionRow['sub']): void;
  closeSubscriptionDialog(this: AppShellState): void;
  submitSubscription(this: AppShellState): Promise<void>;
  onSubscriptionNameInput(this: AppShellState): Promise<void>;
  subscriptionNameStatus(this: AppShellState): { kind: 'empty' | 'matched' | 'custom'; categoryLabel?: string | undefined };
  skipSubscriptionPeriod(this: AppShellState, id: string): Promise<void>;
  pauseSubscription(this: AppShellState, id: string): Promise<void>;
  formatSubscriptionFrequency(this: AppShellState, sub: SubscriptionRow['sub']): string;
  formatRating(this: AppShellState, rating: number): string;
  cancelShopping(this: AppShellState, id: string): Promise<void>;
  addSuggestion(this: AppShellState, sug: Suggestion): Promise<void>;
  reasonLabel(reason: 'expiring_soon' | 'low_stock' | 'subscription_due'): string;
  sourceLabel(source: string | undefined): string;
  lineSubtotal(row: { quantity?: number; unitPrice?: number }): number;
  shoppingPlannedTotal(this: AppShellState): number;
  formatPrice(amount: number | null | undefined): string;
  formatNormalizedPrice(this: AppShellState, np: NormalizedPrice | undefined): string;
  normalizedForRow(this: AppShellState, row: {
    unitPrice?: number;
    packageSize?: number;
    packageContentUnit?: string;
  }): NormalizedPrice | undefined;
  shoppingModalNormalized(this: AppShellState): NormalizedPrice | undefined;
  shoppingModalPriceDelta(this: AppShellState): number | undefined;
  sourceNameSuggestions(this: AppShellState): string[];
  unitSuggestions(this: AppShellState): string[];
  packageContentUnitSuggestions(this: AppShellState): string[];
  dotClass(status: ExpiryStatus): string;
  badgeClass(status: ExpiryStatus): string;
  expiryLabel(info: ExpiryInfo): string;
  locationLabel(location: string): string;
  categoryIcon(category: string | undefined): string;
  locationIcon(location: string | undefined): string;
  rowSummary(row: ItemRow): string;
  formatDate(ms: number | null): string;
  formatBatchQuantity(this: AppShellState, batch: Batch): string;
  formatPreferredBatchQty(this: AppShellState, batch: Batch): string;
  formatPreferredItemTotal(this: AppShellState, detail: DetailView): string;
  preferredQtyForBatch(this: AppShellState, batch: Batch): { qty: number; unit: string };
  formatPerPackagingSpec(this: AppShellState, batch: Batch): string;
  formatNumberCompact(this: AppShellState, n: number): string;
  consumeStepLabelForBatch(this: AppShellState, batch: Batch): string;
  totalPackagingLabel(this: AppShellState, detail: DetailView): string;
  consumeStepLabel(this: AppShellState, unit: string | undefined): string;
  consumePackaging(this: AppShellState, batchId: string): Promise<void>;
  batchUsagePattern(this: AppShellState, batch: Batch): 'unit_per_use' | 'continuous';
  isContinuous(this: AppShellState, batch: Batch): boolean;
  isContinuousAndOpened(this: AppShellState, batch: Batch): boolean;
  isContinuousAndUnopened(this: AppShellState, batch: Batch): boolean;
  itemIsContinuous(this: AppShellState): boolean;
  firstOpenedBatch(this: AppShellState): Batch | undefined;
  firstUnopenedBatch(this: AppShellState): Batch | undefined;
  finishOpenedBatch(this: AppShellState): Promise<void>;
  openFirstBatch(this: AppShellState): Promise<void>;
  openBatchEdit(this: AppShellState, batchId: string): Promise<void>;
  closeBatchEdit(this: AppShellState): void;
  submitBatchEdit(this: AppShellState): Promise<void>;
  applySizePreset(this: AppShellState, size: number): void;
  saveMinQuantity(this: AppShellState): Promise<void>;
  showToast(this: AppShellState, msg: string): void;
}

const EMPTY_VIEW: InventoryView = {
  groups: [],
  todayPanel: [],
  counts: { total: 0, expired: 0, warning: 0, fresh: 0 },
};

const EMPTY_SHOPPING: ShoppingView = {
  planned: [],
  ordered: [],
  inTransit: [],
  recentReceived: [],
  counts: { planned: 0, ordered: 0, inTransit: 0, recentReceived: 0, total: 0 },
};

const EMPTY_SUBSCRIPTIONS: SubscriptionView = {
  active: [],
  inactive: [],
  counts: { active: 0, inactive: 0, total: 0 },
};

const FREQUENCY_LABELS: Record<SubscriptionFrequency, string> = {
  weekly: '每週',
  biweekly: '每兩週',
  monthly: '每月',
  custom: '自訂',
};

const SOURCE_LABELS: Record<OrderSource, string> = {
  physical_store: '實體店',
  online: '網購',
  team_buy: '團購',
  farm: '小農',
  subscription: '訂閱',
  homegrown: '自家種',
  gift: '收禮',
  other: '其他',
};

// 採買清單名稱輸入 → 歷史價格查詢的 debounce
// 200ms 足夠避開連續輸入時的多次查詢，又不至於讓使用者感到延遲
const SHOPPING_HINT_DEBOUNCE = (() => {
  let handle: ReturnType<typeof setTimeout> | null = null;
  return {
    run(fn: () => void | Promise<void>): void {
      if (handle !== null) clearTimeout(handle);
      handle = setTimeout(() => {
        handle = null;
        void fn();
      }, 200);
    },
    cancel(): void {
      if (handle !== null) {
        clearTimeout(handle);
        handle = null;
      }
    },
  };
})();

const EMPTY_DETAIL: DetailView = {
  open: false,
  item: null,
  batches: [],
  expiry: { status: 'fresh', daysLeft: null },
  totalQuantity: 0,
};

Alpine.data('appShell', (): AppShellState => ({
  ready: false,
  version: APP_VERSION,
  buildTime: formatBuildTime(BUILD_TIME),
  commitHash: COMMIT_HASH,
  platformName: 'detecting…',
  currentTab: 'today',
  view: EMPTY_VIEW,
  shopping: EMPTY_SHOPPING,
  subscriptions: EMPTY_SUBSCRIPTIONS,
  suggestions: [],
  knownLocations: [],
  warningDays: 3,
  modal: {
    open: false,
    name: '',
    quantity: 1,
    submitting: false,
    error: null,
  },
  selectedLocation: null,
  locationAddDialog: {
    open: false,
    name: '',
    submitting: false,
    error: null,
  },
  shoppingModal: {
    open: false,
    name: '',
    quantity: 1,
    unit: '',
    unitPrice: null,
    packageSize: null,
    packageContentUnit: '',
    presetSizes: [],
    presetSizeUnit: '',
    showCustomPackaging: false,
    submitting: false,
    error: null,
    lastPriceHint: null,
    hintLoading: false,
  },
  shoppingContext: {
    source: '',
    sourceName: '',
  },
  shoppingContextDialog: {
    open: false,
    source: '',
    sourceName: '',
  },
  detail: { ...EMPTY_DETAIL },
  batchEdit: {
    open: false,
    itemId: null,
    batchId: null,
    quantity: 0,
    unit: '',
    packageSize: null,
    packageUnit: '',
    expiresAt: '',
    presetSizes: [],
    presetSizeUnit: '',
    showCustomPackaging: false,
    submitting: false,
  },
  minQuantityDraft: 0,
  receiveDialog: {
    open: false,
    shoppingItemId: null,
    name: '',
    unitPrice: null,
    defaultUnitPrice: null,
    packageSize: null,
    packageContentUnit: '',
    sourceName: '',
    submitting: false,
  },
  inTransitDialog: {
    open: false,
    shoppingItemId: null,
    name: '',
    expectedArrival: '',
    submitting: false,
  },
  subscriptionDialog: {
    open: false,
    editingId: null,
    defaultName: '',
    knowledgeId: '',
    defaultQuantity: 1,
    defaultUnit: '',
    source: '',
    frequency: 'biweekly',
    customDays: 14,
    nextDelivery: '',
    leadDays: 3,
    submitting: false,
    error: null,
  },
  settings: {
    exportBusy: false,
    importBusy: false,
    importMessage: null,
    importError: null,
  },
  datalistOptions: { sourceNames: [], units: [], packageContentUnits: [] },
  pantryKnowledgeOptions: [] as PantryKnowledge[],
  toast: null,
  theme: 'system' as ThemeMode,
  notificationsEnabled: true,

  async init() {
    const platform = await getPlatform();
    this.platformName = platform.platformName;
    const settings = await getOrCreateSettings();
    this.warningDays = settings.expiryWarningDays;
    this.notificationsEnabled = settings.notificationsEnabled;
    this.shoppingContext = {
      source: (settings.activeShoppingSource as OrderSource | undefined) ?? '',
      sourceName: settings.activeShoppingSourceName ?? '',
    };
    this.theme = settings.theme ?? 'system';
    applyTheme(this.theme);
    setupSystemThemeListener(() => {
      // theme === 'system' 才需跟著 OS 切換；強制覆寫的 light/dark 不受影響
      if (this.theme === 'system') applyTheme('system');
    });
    await initI18n(settings.language);
    await seedKnowledgeIfEmpty();
    await mergeDuplicateActiveItems();
    await this.refresh();
    this.pantryKnowledgeOptions = await db.pantryKnowledge.toArray();
    this.ready = true;
  },

  async setTheme(value: ThemeMode) {
    this.theme = value;
    applyTheme(value);
    await updateSettings({ theme: value });
  },

  async setNotificationsEnabled(value: boolean) {
    this.notificationsEnabled = value;
    await updateSettings({ notificationsEnabled: value });
  },

  async setExpiryWarningDays(value: number) {
    const safe = Math.max(1, Math.min(7, Math.floor(value)));
    this.warningDays = safe;
    await updateSettings({ expiryWarningDays: safe });
  },

  async refresh() {
    const settings = await getOrCreateSettings();
    this.knownLocations = settings.knownLocations;
    this.warningDays = settings.expiryWarningDays;
    this.view = await loadInventoryView();
    this.shopping = await loadShoppingView();
    this.subscriptions = await loadSubscriptionView();
    this.suggestions = await loadSuggestions();
    this.datalistOptions = await loadShoppingDatalistOptions();
  },

  switchTab(tab: Tab) {
    this.currentTab = tab;
  },

  selectLocation(location: string | null) {
    this.selectedLocation = location;
  },

  visibleLocationGroups(): LocationGroup[] {
    if (this.selectedLocation === null) return this.view.groups;
    return this.view.groups.filter((g) => g.location === this.selectedLocation);
  },

  locationCards(): LocationCard[] {
    // 顯示所有 knownLocations（即使沒 item）+ 從 view.groups 帶 count
    const countMap = new Map<string, number>();
    for (const g of this.view.groups) {
      countMap.set(g.location, g.rows.length);
    }
    const seen = new Set<string>();
    const cards: LocationCard[] = [];
    for (const loc of this.knownLocations) {
      if (seen.has(loc)) continue;
      seen.add(loc);
      cards.push({ location: loc, count: countMap.get(loc) ?? 0 });
    }
    // 補上 view.groups 中存在但不在 knownLocations 的（過去 quickAdd 自動 remember 的）
    for (const g of this.view.groups) {
      if (seen.has(g.location)) continue;
      seen.add(g.location);
      cards.push({ location: g.location, count: g.rows.length });
    }
    return cards;
  },

  openLocationAdd() {
    this.locationAddDialog = { open: true, name: '', submitting: false, error: null };
  },

  closeLocationAdd() {
    this.locationAddDialog.open = false;
  },

  selectedLocationItemCount(): number {
    if (this.selectedLocation === null) return 0;
    const group = this.view.groups.find((g) => g.location === this.selectedLocation);
    return group?.rows.length ?? 0;
  },

  async deleteSelectedLocation() {
    const loc = this.selectedLocation;
    if (!loc) return;
    if (this.selectedLocationItemCount() > 0) {
      this.showToast('請先把物品移走或丟棄');
      return;
    }
    if (!confirm(`刪除位置「${loc}」？`)) return;
    const settings = await getOrCreateSettings();
    const next = await updateSettings({
      knownLocations: settings.knownLocations.filter((l) => l !== loc),
    });
    this.knownLocations = next.knownLocations;
    this.selectLocation(null);
    this.showToast('已刪除位置');
  },

  async submitLocationAdd() {
    const name = this.locationAddDialog.name.trim();
    if (!name) {
      this.locationAddDialog.error = '請輸入位置名稱';
      return;
    }
    this.locationAddDialog.submitting = true;
    this.locationAddDialog.error = null;
    try {
      const settings = await getOrCreateSettings();
      const next = settings.knownLocations.includes(name)
        ? settings
        : await updateSettings({ knownLocations: [...settings.knownLocations, name] });
      this.knownLocations = next.knownLocations;
      this.closeLocationAdd();
      // 自動進入新位置（讓使用者立刻可加庫存）
      this.selectLocation(name);
      this.showToast(`已新增位置：${name}`);
    } catch (err) {
      this.locationAddDialog.error = err instanceof Error ? err.message : '新增失敗';
    } finally {
      this.locationAddDialog.submitting = false;
    }
  },

  openQuickAdd() {
    // 一定從第二層（已選 location）進來；location 直接用 selectedLocation，
    // 不在 modal 裡再問一次（呼應 minimal entry 原則）。
    this.modal = {
      open: true,
      name: '',
      quantity: 1,
      submitting: false,
      error: null,
    };
  },

  closeQuickAdd() {
    this.modal.open = false;
  },

  async submitQuickAdd() {
    const name = this.modal.name.trim();
    if (!name) {
      this.modal.error = '請輸入物品名稱';
      return;
    }
    this.modal.submitting = true;
    this.modal.error = null;
    try {
      const location = this.selectedLocation ?? '';
      const result = await quickAdd({
        name,
        quantity: this.modal.quantity > 0 ? this.modal.quantity : 1,
        ...(location ? { location } : {}),
      });
      this.closeQuickAdd();
      await this.refresh();
      this.showToast(result.isNewItem ? `加入「${name}」` : `「${name}」追加一批`);
    } catch (err) {
      this.modal.error = err instanceof Error ? err.message : '加入失敗';
    } finally {
      this.modal.submitting = false;
    }
  },

  async openDetail(itemId: string) {
    const item = await db.items.get(itemId);
    if (!item) return;
    this.detail = buildDetail(item, this.warningDays);
    this.minQuantityDraft = item.minQuantity ?? 0;
  },

  closeDetail() {
    this.detail = { ...EMPTY_DETAIL };
    this.minQuantityDraft = 0;
  },

  async reloadDetail() {
    if (!this.detail.item) return;
    const fresh = await db.items.get(this.detail.item.id);
    if (!fresh) {
      this.closeDetail();
      await this.refresh();
      return;
    }
    this.detail = buildDetail(fresh, this.warningDays);
    this.minQuantityDraft = fresh.minQuantity ?? 0;
  },

  async openBatchEdit(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    const batch = item.batches.find((b) => b.id === batchId);
    if (!batch) return;
    // 從 PantryKnowledge 撈常見規格 + 比價單位（用於 chip preset）
    const knowledge = await lookupKnowledge(item.name);
    const presetSizes = knowledge?.commonPackageSizes ?? [];
    const presetSizeUnit = knowledge?.compareBaseUnit ?? '';
    // 自訂行只在使用者已有非 preset 值或無 chip preset 時預設展開
    const hasCustomValue =
      batch.packageSize !== undefined &&
      !presetSizes.includes(batch.packageSize);
    this.batchEdit = {
      open: true,
      itemId: item.id,
      batchId,
      quantity: batch.quantity,
      unit: batch.unit,
      packageSize: batch.packageSize ?? null,
      packageUnit: batch.packageUnit ?? '',
      expiresAt: batch.expiresAt ? toDateInputValue(batch.expiresAt) : '',
      presetSizes,
      presetSizeUnit,
      showCustomPackaging: presetSizes.length === 0 || hasCustomValue,
      submitting: false,
    };
  },

  applySizePreset(size: number) {
    // v0.3.4: batch 永遠用 packaging storage（quantity=瓶/盒/包數），
    // chip 只設 packageSize（每包裝多少 base unit）+ packageUnit（base unit）為比價 metadata。
    // 完全不動 quantity / unit — 使用者上方「4 瓶」「1 盒」保留。
    const baseUnit = this.batchEdit.presetSizeUnit;
    if (!baseUnit) return;
    this.batchEdit.packageSize = size;
    this.batchEdit.packageUnit = baseUnit;
    this.batchEdit.showCustomPackaging = false;
  },

  closeBatchEdit() {
    this.batchEdit.open = false;
  },

  async submitBatchEdit() {
    const { itemId, batchId, quantity, unit, packageSize, packageUnit, expiresAt } = this.batchEdit;
    if (!itemId || !batchId) return;
    const safeQty = Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
    this.batchEdit.submitting = true;
    try {
      const patch: Parameters<typeof updateBatch>[2] = {
        quantity: safeQty,
        unit: unit.trim(),
      };
      // packageSize: null 清除；> 0 設值；其他不動（避免覆蓋成 0）
      if (packageSize === null || packageSize === undefined) {
        patch.packageSize = null;
      } else if (packageSize > 0) {
        patch.packageSize = packageSize;
      }
      patch.packageUnit = packageUnit.trim() || null;
      if (expiresAt) {
        const d = new Date(expiresAt + 'T23:59:59');
        if (!Number.isNaN(d.getTime())) patch.expiresAt = d;
      } else {
        patch.expiresAt = null;
      }
      await updateBatch(itemId, batchId, patch);
      this.batchEdit.open = false;
      await this.refresh();
      await this.reloadDetail();
      this.showToast('已更新批次');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '更新失敗');
    } finally {
      this.batchEdit.submitting = false;
    }
  },

  async saveMinQuantity() {
    const item = this.detail.item;
    if (!item) return;
    const draft = this.minQuantityDraft;
    const safe = Number.isFinite(draft) ? Math.max(0, Math.floor(draft)) : 0;
    await setItemMinQuantity(item.id, safe);
    await this.refresh();
    await this.reloadDetail();
    this.showToast(safe > 0 ? `常備需求量：${safe}` : '已取消常備');
  },

  async consumeOne() {
    const item = this.detail.item;
    if (!item) return;
    const first = this.detail.batches[0];
    if (!first) return;
    // v0.3.4：batch 是 packaging storage，「用 1 次」要扣 packaging fraction
    let amount: number;
    let label: string;
    if (first.packageSize && first.packageSize > 0 && first.packageUnit) {
      const step = consumeStep(first.packageUnit);
      amount = step.amount / first.packageSize; // 100ml / 936 ≈ 0.107 瓶
      label = step.label;
    } else {
      const step = consumeStep(first.unit);
      amount = step.amount;
      label = step.label;
    }
    const taken = await consumeFifo(item.id, amount);
    if (taken === 0) {
      this.showToast('已經沒有庫存了');
      return;
    }
    await this.refresh();
    await this.reloadDetail();
    this.showToast(`扣 ${label}（FIFO）`);
  },

  async consumeOneFullPackaging() {
    const item = this.detail.item;
    if (!item) return;
    const first = this.detail.batches[0];
    if (!first) return;
    // v0.3.4：1 整 packaging = 扣 1 個 batch.quantity（packaging count）
    const taken = await consumeFifo(item.id, 1);
    if (taken === 0) {
      this.showToast('已經沒有庫存了');
      return;
    }
    await this.refresh();
    await this.reloadDetail();
    this.showToast(`扣 1${first.unit}（FIFO）`);
  },

  async consumeBatchOne(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    const batch = item.batches.find((b) => b.id === batchId);
    if (!batch) return;
    // v0.3.4：「用 1 次」對 packaging storage：
    // - 有 packageSize → 扣 step.amount / packageSize（小數 packaging fraction）
    // - 沒 → 扣 step.amount packaging unit
    let amount: number;
    if (batch.packageSize && batch.packageSize > 0 && batch.packageUnit) {
      const step = consumeStep(batch.packageUnit);
      amount = Math.min(step.amount / batch.packageSize, batch.quantity);
    } else {
      const step = consumeStep(batch.unit);
      amount = Math.min(step.amount, batch.quantity);
    }
    const taken = await consumeBatchAmount(item.id, batchId, amount);
    if (taken === 0) return;
    await this.refresh();
    await this.reloadDetail();
  },

  async consumeBatchAll(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    const batch = item.batches.find((b) => b.id === batchId);
    if (!batch) return;
    await consumeBatchAmount(item.id, batchId, batch.quantity);
    await this.refresh();
    await this.reloadDetail();
    this.showToast('整批已用完');
  },

  async openBatch(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    await markBatchOpened(item.id, batchId);
    await this.refresh();
    await this.reloadDetail();
    this.showToast('已標記為「已開封」');
  },

  async discardThisBatch(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    if (!confirm('確定丟掉這批？此操作會記錄為過期 / 壞掉。')) return;
    await discardBatch(item.id, batchId, 'expired');
    await this.refresh();
    await this.reloadDetail();
    this.showToast('已丟掉這批');
  },

  async discardThisItem() {
    const item = this.detail.item;
    if (!item) return;
    if (!confirm(`確定要把「${item.name}」整項丟掉？`)) return;
    await discardItem(item.id);
    this.closeDetail();
    await this.refresh();
    this.showToast(`已丟掉「${item.name}」`);
  },

  async exportBackup() {
    this.settings.exportBusy = true;
    try {
      const { blob, filename } = await exportBackupBlob(APP_VERSION);
      const platform = await getPlatform();
      await platform.shareFile(blob, filename);
      this.showToast('備份已輸出');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '匯出失敗');
    } finally {
      this.settings.exportBusy = false;
    }
  },

  openShoppingAdd() {
    this.shoppingModal = {
      open: true,
      name: '',
      quantity: 1,
      unit: '',
      unitPrice: null,
      packageSize: null,
      packageContentUnit: '',
      presetSizes: [],
      presetSizeUnit: '',
      showCustomPackaging: false,
      submitting: false,
      error: null,
      lastPriceHint: null,
      hintLoading: false,
    };
  },

  closeShoppingAdd() {
    this.shoppingModal.open = false;
  },

  onShoppingNameInput() {
    const name = this.shoppingModal.name.trim();
    SHOPPING_HINT_DEBOUNCE.cancel();
    if (!name) {
      this.shoppingModal.lastPriceHint = null;
      this.shoppingModal.hintLoading = false;
      return;
    }
    this.shoppingModal.hintLoading = true;
    SHOPPING_HINT_DEBOUNCE.run(async () => {
      // 名稱可能在 debounce 期間又變了，比對最新值才寫回
      const currentName = this.shoppingModal.name.trim();
      if (currentName !== name) return;
      const [hint, knowledge] = await Promise.all([
        lookupLastUnitPrice(currentName),
        lookupKnowledge(currentName),
      ]);
      if (this.shoppingModal.name.trim() !== currentName) return;
      this.shoppingModal.lastPriceHint = hint ?? null;
      // 若還沒填過 unit（包裝單位，例：盒/罐/包），用 PantryKnowledge.defaultUnit 預填
      if (!this.shoppingModal.unit && knowledge?.defaultUnit) {
        this.shoppingModal.unit = knowledge.defaultUnit;
      }
      // 若還沒填過 packageContentUnit（規格單位，例：顆/ml/g），用 compareBaseUnit 預填
      if (!this.shoppingModal.packageContentUnit && knowledge?.compareBaseUnit) {
        this.shoppingModal.packageContentUnit = knowledge.compareBaseUnit;
      }
      // chip preset（跟 batch edit dialog 同邏輯）
      this.shoppingModal.presetSizes = knowledge?.commonPackageSizes ?? [];
      this.shoppingModal.presetSizeUnit = knowledge?.compareBaseUnit ?? '';
      const hasCustom =
        this.shoppingModal.packageSize !== null &&
        !this.shoppingModal.presetSizes.includes(this.shoppingModal.packageSize);
      this.shoppingModal.showCustomPackaging =
        this.shoppingModal.presetSizes.length === 0 || hasCustom;
      this.shoppingModal.hintLoading = false;
    });
  },

  applyLastPriceHint() {
    const hint = this.shoppingModal.lastPriceHint;
    if (!hint) return;
    this.shoppingModal.unitPrice = hint.unitPrice;
  },

  applyShoppingSizePreset(size: number) {
    // 跟 batch edit dialog 同邏輯：只設 packageSize / packageContentUnit，不動 quantity / unit
    this.shoppingModal.packageSize = size;
    if (this.shoppingModal.presetSizeUnit) {
      this.shoppingModal.packageContentUnit = this.shoppingModal.presetSizeUnit;
    }
    this.shoppingModal.showCustomPackaging = false;
  },

  async submitShoppingAdd() {
    const name = this.shoppingModal.name.trim();
    if (!name) {
      this.shoppingModal.error = '請輸入物品名稱';
      return;
    }
    this.shoppingModal.submitting = true;
    this.shoppingModal.error = null;
    try {
      const input: Parameters<typeof addShoppingItem>[0] = {
        name,
        quantity: this.shoppingModal.quantity > 0 ? this.shoppingModal.quantity : 1,
      };
      if (this.shoppingModal.unit.trim()) {
        input.unit = this.shoppingModal.unit.trim();
      }
      if (this.shoppingModal.unitPrice !== null && this.shoppingModal.unitPrice >= 0) {
        input.unitPrice = this.shoppingModal.unitPrice;
      }
      // 自動套用當前採買 trip 的 source / sourceName（從 chip 設定）
      if (this.shoppingContext.source) {
        input.source = this.shoppingContext.source;
      }
      if (this.shoppingContext.sourceName.trim()) {
        input.sourceName = this.shoppingContext.sourceName.trim();
      }
      if (this.shoppingModal.packageSize !== null && this.shoppingModal.packageSize > 0) {
        input.packageSize = this.shoppingModal.packageSize;
      }
      if (this.shoppingModal.packageContentUnit.trim()) {
        input.packageContentUnit = this.shoppingModal.packageContentUnit.trim();
      }
      await addShoppingItem(input);
      this.closeShoppingAdd();
      await this.refresh();
      this.showToast(`加入採買清單：${name}`);
    } catch (err) {
      this.shoppingModal.error = err instanceof Error ? err.message : '加入失敗';
    } finally {
      this.shoppingModal.submitting = false;
    }
  },

  async openReceiveDialog(id: string) {
    const row = await db.shoppingItems.get(id);
    if (!row) return;
    const existing = row.unitPrice ?? null;
    let defaultPrice = existing;
    let defaultPkgSize: number | null = row.packageSize ?? null;
    let defaultPkgUnit = row.packageContentUnit ?? '';
    if (existing === null) {
      const hint = await lookupLastUnitPrice(row.name);
      defaultPrice = hint?.unitPrice ?? null;
      if (defaultPkgSize === null && hint?.packageSize !== undefined) {
        defaultPkgSize = hint.packageSize;
      }
      if (!defaultPkgUnit && hint?.packageContentUnit) {
        defaultPkgUnit = hint.packageContentUnit;
      }
    }
    if (!defaultPkgUnit) {
      const knowledge = await lookupKnowledge(row.name);
      if (knowledge?.compareBaseUnit) {
        defaultPkgUnit = knowledge.compareBaseUnit;
      }
    }
    this.receiveDialog = {
      open: true,
      shoppingItemId: id,
      name: row.name,
      unitPrice: defaultPrice,
      defaultUnitPrice: defaultPrice,
      packageSize: defaultPkgSize,
      packageContentUnit: defaultPkgUnit,
      sourceName: row.sourceName ?? '',
      submitting: false,
    };
  },

  closeReceiveDialog() {
    this.receiveDialog.open = false;
  },

  async submitReceive() {
    const id = this.receiveDialog.shoppingItemId;
    if (!id) return;
    this.receiveDialog.submitting = true;
    try {
      const overrides: Parameters<typeof markReceived>[1] = {};
      if (this.receiveDialog.unitPrice !== null && this.receiveDialog.unitPrice >= 0) {
        overrides.unitPrice = this.receiveDialog.unitPrice;
      }
      if (this.receiveDialog.packageSize !== null && this.receiveDialog.packageSize > 0) {
        overrides.packageSize = this.receiveDialog.packageSize;
      }
      if (this.receiveDialog.packageContentUnit.trim()) {
        overrides.packageContentUnit = this.receiveDialog.packageContentUnit.trim();
      }
      if (this.receiveDialog.sourceName.trim()) {
        overrides.sourceName = this.receiveDialog.sourceName.trim();
      }
      await markReceived(id, overrides);
      this.closeReceiveDialog();
      await this.refresh();
      this.showToast('已入庫');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '入庫失敗');
    } finally {
      this.receiveDialog.submitting = false;
    }
  },

  async receiveShopping(id: string) {
    // 後援路徑：直接入庫（沒填單價）。UI 預設改走 openReceiveDialog
    try {
      await markReceived(id);
      await this.refresh();
      this.showToast('已入庫');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '入庫失敗');
    }
  },

  async openInTransitDialog(id: string) {
    const row = await db.shoppingItems.get(id);
    if (!row) return;
    // 預填現有 ETA（轉成 yyyy-mm-dd）；無則空字串。
    let etaStr = '';
    if (row.expectedArrival) {
      const d = new Date(row.expectedArrival);
      etaStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    this.inTransitDialog = {
      open: true,
      shoppingItemId: id,
      name: row.name,
      expectedArrival: etaStr,
      submitting: false,
    };
  },

  closeInTransitDialog() {
    this.inTransitDialog.open = false;
  },

  async submitInTransit() {
    const id = this.inTransitDialog.shoppingItemId;
    if (!id) return;
    this.inTransitDialog.submitting = true;
    try {
      const opts: Parameters<typeof markInTransit>[1] = {};
      const etaStr = this.inTransitDialog.expectedArrival;
      if (etaStr) {
        const eta = new Date(etaStr);
        if (!Number.isNaN(eta.getTime())) opts.expectedArrival = eta;
      }
      // 套用當前 trip context 作為 source（若 row 還沒有）
      const row = await db.shoppingItems.get(id);
      if (row && !row.source && this.shoppingContext.source) {
        opts.source = this.shoppingContext.source;
      }
      if (row && !row.sourceName && this.shoppingContext.sourceName) {
        opts.sourceName = this.shoppingContext.sourceName;
      }
      await markInTransit(id, opts);
      this.closeInTransitDialog();
      await this.refresh();
      this.showToast('已標記在途中');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '標記失敗');
    } finally {
      this.inTransitDialog.submitting = false;
    }
  },

  openSubscriptionAdd() {
    const today = new Date();
    const next = new Date(today.getTime() + 14 * 86_400_000);
    const yyyymmdd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.subscriptionDialog = {
      open: true,
      editingId: null,
      defaultName: '',
      knowledgeId: '',
      defaultQuantity: 1,
      defaultUnit: '',
      source: '',
      frequency: 'biweekly',
      customDays: 14,
      nextDelivery: yyyymmdd(next),
      leadDays: 3,
      submitting: false,
      error: null,
    };
  },

  openSubscriptionEdit(sub) {
    // 把既有 sub 各欄位塞進 dialog state，讓使用者直接改
    const yyyymmdd = (ms: number) => {
      const d = new Date(ms);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    this.subscriptionDialog = {
      open: true,
      editingId: sub.id,
      defaultName: sub.defaultName,
      knowledgeId: sub.knowledgeId,
      defaultQuantity: sub.defaultQuantity,
      defaultUnit: sub.defaultUnit,
      source: sub.source,
      frequency: sub.frequency,
      customDays: sub.customDays ?? 14,
      nextDelivery: yyyymmdd(sub.nextDelivery),
      leadDays: sub.leadDays,
      submitting: false,
      error: null,
    };
  },

  closeSubscriptionDialog() {
    this.subscriptionDialog.open = false;
  },
  async onSubscriptionNameInput() {
    const d = this.subscriptionDialog;
    const name = d.defaultName.trim();
    if (!name) {
      d.knowledgeId = '';
      return;
    }
    const knowledge = await lookupKnowledge(name);
    if (knowledge) {
      d.knowledgeId = knowledge.id;
    } else {
      d.knowledgeId = '';
    }
  },

  subscriptionNameStatus(): { kind: 'empty' | 'matched' | 'custom'; categoryLabel?: string | undefined } {
    const d = this.subscriptionDialog;
    const name = d.defaultName.trim();
    if (!name) return { kind: 'empty' };
    if (d.knowledgeId && !d.knowledgeId.startsWith('custom:')) {
      // 從 pantryKnowledgeOptions 查詢 category
      const CATEGORY_LABEL_MAP: Record<string, string> = {
        vegetable: '蔬菜',
        fruit: '水果',
        meat: '肉類',
        seafood: '海鮮',
        dairy_egg: '蛋奶',
        condiment: '調味',
        dry_good: '乾貨',
        frozen: '冷凍',
        beverage: '飲料',
        household: '家用',
        other: '其他',
      };
      const kn = this.pantryKnowledgeOptions.find((k) => k.id === d.knowledgeId);
      const categoryLabel = kn ? (CATEGORY_LABEL_MAP[kn.category] ?? kn.category) : undefined;
      return { kind: 'matched', categoryLabel };
    }
    return { kind: 'custom' };
  },

  async submitSubscription() {
    const d = this.subscriptionDialog;
    const name = d.defaultName.trim();
    if (!name) {
      d.error = '請輸入商品名稱';
      return;
    }
    if (d.defaultQuantity <= 0) {
      d.error = '數量需大於 0';
      return;
    }
    if (!d.defaultUnit.trim()) {
      d.error = '請輸入單位';
      return;
    }
    if (!d.source.trim()) {
      d.error = '請輸入訂閱來源';
      return;
    }
    if (d.frequency === 'custom' && d.customDays <= 0) {
      d.error = '自訂頻率需大於 0 天';
      return;
    }
    let knowledgeId = d.knowledgeId.trim();
    if (!knowledgeId) {
      // 嘗試自動推 canonical
      const knowledge = await lookupKnowledge(name);
      if (knowledge) knowledgeId = knowledge.id;
      else knowledgeId = `custom:${name.toLowerCase()}`;
    }
    const eta = d.nextDelivery ? new Date(d.nextDelivery) : undefined;
    const baseInput: CreateSubscriptionInput = {
      knowledgeId,
      defaultName: name,
      frequency: d.frequency,
      source: d.source.trim(),
      defaultQuantity: d.defaultQuantity,
      defaultUnit: d.defaultUnit.trim(),
      leadDays: d.leadDays,
    };
    if (d.frequency === 'custom') baseInput.customDays = d.customDays;
    if (eta && !Number.isNaN(eta.getTime())) baseInput.nextDelivery = eta;

    d.submitting = true;
    d.error = null;
    try {
      if (d.editingId) {
        const updateInput: UpdateSubscriptionInput = {
          ...baseInput,
          subscriptionId: d.editingId,
        };
        await updateSubscription(updateInput);
        this.closeSubscriptionDialog();
        await this.refresh();
        this.showToast(`已更新訂閱：${name}`);
      } else {
        await createSubscription(baseInput);
        this.closeSubscriptionDialog();
        await this.refresh();
        this.showToast(`已加入訂閱：${name}`);
      }
    } catch (err) {
      d.error = err instanceof Error ? err.message : (d.editingId ? '更新訂閱失敗' : '建立訂閱失敗');
    } finally {
      d.submitting = false;
    }
  },

  async skipSubscriptionPeriod(id: string) {
    if (!confirm('跳過下一期？此期不會建議補單，下下期照常。')) return;
    try {
      await advanceSubscription(id, 'manual');
      await this.refresh();
      this.showToast('已跳過下一期');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '操作失敗');
    }
  },

  async pauseSubscription(id: string) {
    if (!confirm('暫停訂閱？暫停後不會再建議補單；可再 [新增] 重啟。')) return;
    try {
      await deactivateSubscription(id);
      await this.refresh();
      this.showToast('已暫停');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '操作失敗');
    }
  },

  formatSubscriptionFrequency(sub) {
    if (sub.frequency === 'custom') {
      return `每 ${sub.customDays ?? 0} 天`;
    }
    return FREQUENCY_LABELS[sub.frequency];
  },

  formatRating(rating: number): string {
    const full = Math.floor(rating);
    const half = rating - full >= 0.25 && rating - full < 0.75 ? 1 : 0;
    const empty = 5 - full - half;
    return '★'.repeat(full) + (half ? '⯨' : '') + '☆'.repeat(empty);
  },

  async cancelShopping(id: string) {
    if (!confirm('從採買清單移除這項？')) return;
    await cancelShoppingItem(id);
    await this.refresh();
    this.showToast('已從清單移除');
  },

  async addSuggestion(sug: Suggestion) {
    const input: Parameters<typeof addShoppingItem>[0] = {
      name: sug.name,
      reason: sug.reason,
    };
    if (sug.suggestedQuantity !== undefined) input.quantity = sug.suggestedQuantity;
    if (sug.suggestedUnit !== undefined) input.unit = sug.suggestedUnit;
    if (sug.subscriptionId) input.subscriptionId = sug.subscriptionId;
    await addShoppingItem(input);
    await this.refresh();
    this.showToast(`已加入清單：${sug.name}`);
  },

  reasonLabel(reason) {
    if (reason === 'expiring_soon') return '快過期';
    if (reason === 'low_stock') return '已用完';
    if (reason === 'subscription_due') return '訂閱到期';
    return '';
  },

  sourceLabel(source: string | undefined) {
    if (!source) return '';
    return SOURCE_LABELS[source as OrderSource] ?? source;
  },

  lineSubtotal(row: { quantity?: number; unitPrice?: number }): number {
    if (row.unitPrice === undefined || row.unitPrice < 0) return 0;
    const qty = row.quantity && row.quantity > 0 ? row.quantity : 1;
    return Math.round(row.unitPrice * qty);
  },

  shoppingPlannedTotal(): number {
    return this.shopping.planned.reduce(
      (sum, row) => sum + this.lineSubtotal(row),
      0,
    );
  },

  formatPrice(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return '';
    if (!Number.isFinite(amount)) return '';
    return `NT$ ${Math.round(amount).toLocaleString('en-US')}`;
  },

  formatNormalizedPrice(np: NormalizedPrice | undefined): string {
    if (!np) return '';
    // 小於 1 元保留兩位；> 1 元保留一位足夠
    const value =
      np.pricePerCompareUnit < 1
        ? np.pricePerCompareUnit.toFixed(2)
        : np.pricePerCompareUnit.toFixed(1);
    return `每 ${np.compareUnitLabel} NT$ ${value}`;
  },

  normalizedForRow(row: {
    unit?: string;
    unitPrice?: number;
    packageSize?: number;
    packageContentUnit?: string;
  }): NormalizedPrice | undefined {
    return normalizeUnitPrice(
      row.unitPrice,
      row.packageSize,
      row.packageContentUnit,
      row.unit,
    );
  },

  shoppingModalNormalized(): NormalizedPrice | undefined {
    return normalizeUnitPrice(
      this.shoppingModal.unitPrice ?? undefined,
      this.shoppingModal.packageSize ?? undefined,
      this.shoppingModal.packageContentUnit || undefined,
      this.shoppingModal.unit || undefined,
    );
  },

  shoppingModalPriceDelta(): number | undefined {
    const current = this.shoppingModalNormalized();
    const last = this.shoppingModal.lastPriceHint?.normalized;
    return priceDeltaPct(current, last);
  },

  sourceNameSuggestions(): string[] {
    // user history 排前，內建預設排後，去重後最多 20 條
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of [
      ...this.datalistOptions.sourceNames,
      ...SOURCE_NAME_DEFAULTS,
    ]) {
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    return out.slice(0, 20);
  },

  unitSuggestions(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of [...this.datalistOptions.units, ...QUANTITY_UNIT_DEFAULTS]) {
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    return out.slice(0, 20);
  },

  openShoppingContextEdit() {
    this.shoppingContextDialog = {
      open: true,
      source: this.shoppingContext.source,
      sourceName: this.shoppingContext.sourceName,
    };
  },

  closeShoppingContextEdit() {
    this.shoppingContextDialog.open = false;
  },

  async saveShoppingContext() {
    const source = this.shoppingContextDialog.source;
    const sourceName = this.shoppingContextDialog.sourceName.trim();
    this.shoppingContext = { source, sourceName };
    await updateSettings({
      activeShoppingSource: source ? source : undefined,
      activeShoppingSourceName: sourceName ? sourceName : undefined,
    });
    this.shoppingContextDialog.open = false;
    this.showToast(
      sourceName ? `已切換到：${sourceName}` : source ? '已更新採買方式' : '已清除採買來源',
    );
  },

  async clearShoppingContext() {
    this.shoppingContextDialog.source = '';
    this.shoppingContextDialog.sourceName = '';
    await this.saveShoppingContext();
  },

  shoppingContextLabel(): string {
    const { source, sourceName } = this.shoppingContext;
    if (sourceName && source) return `${SOURCE_LABELS[source]}・${sourceName}`;
    if (sourceName) return sourceName;
    if (source) return SOURCE_LABELS[source];
    return '尚未設定';
  },

  contextMatchedPlannedCount(): number {
    return this.shopping.planned.filter((row) =>
      this.shoppingContextMatchesRow(row),
    ).length;
  },

  contextMatchedPlannedTotal(): number {
    return this.shopping.planned
      .filter((row) => this.shoppingContextMatchesRow(row))
      .reduce((sum, row) => sum + this.lineSubtotal(row), 0);
  },

  shoppingContextMatchesRow(row: { source?: string; sourceName?: string }): boolean {
    const ctxName = this.shoppingContext.sourceName.trim();
    const ctxSource = this.shoppingContext.source;
    if (ctxName) return row.sourceName === ctxName;
    if (ctxSource) return row.source === ctxSource && !row.sourceName;
    // 沒設 context 時：認為「沒填 source 也沒填 sourceName」的項是當前
    return !row.source && !row.sourceName;
  },

  async markAllInContextReceived() {
    const matched = this.shopping.planned.filter((row) =>
      this.shoppingContextMatchesRow(row),
    );
    if (matched.length === 0) {
      this.showToast('沒有想買項目可入庫');
      return;
    }
    if (!confirm(`一次入庫 ${matched.length} 樣？此動作不可復原（但每筆 batch 仍可個別丟）。`)) {
      return;
    }
    for (const row of matched) {
      try {
        await markReceived(row.id);
      } catch (err) {
        console.error('markAllInContextReceived: failed for', row.id, err);
      }
    }
    await this.refresh();
    this.showToast(`已入庫 ${matched.length} 樣`);
  },

  packageContentUnitSuggestions(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const v of [
      ...this.datalistOptions.packageContentUnits,
      ...PACKAGE_CONTENT_UNIT_DEFAULTS,
    ]) {
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    return out.slice(0, 20);
  },

  async importBackup(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!confirm('匯入會覆蓋目前所有資料，確定？')) return;
    this.settings.importBusy = true;
    this.settings.importMessage = null;
    this.settings.importError = null;
    try {
      const text = await file.text();
      const result = await importBackupFromText(text);
      this.settings.importMessage = `已匯入 ${result.eventsImported} 筆 events`;
      if (result.warnings.length > 0) {
        this.settings.importMessage += `（${result.warnings.length} 個警告）`;
      }
      await this.refresh();
      this.showToast('備份已匯入');
    } catch (err) {
      this.settings.importError = err instanceof Error ? err.message : '匯入失敗';
    } finally {
      this.settings.importBusy = false;
    }
  },

  dotClass(status) {
    if (status === 'expired') return 'mlh-dot-expired';
    if (status === 'warning') return 'mlh-dot-warning';
    return 'mlh-dot-fresh';
  },

  badgeClass(status) {
    if (status === 'expired') return 'mlh-badge-expired';
    if (status === 'warning') return 'mlh-badge-warning';
    return 'mlh-badge-fresh';
  },

  expiryLabel(info) {
    if (info.daysLeft === null) return '不追蹤期限';
    if (info.daysLeft < 0) return `已過期 ${Math.abs(info.daysLeft)} 天`;
    if (info.daysLeft === 0) return '今天到期';
    if (info.daysLeft === 1) return '明天到期';
    return `還有 ${info.daysLeft} 天`;
  },

  locationLabel(location) {
    return tLocation(location);
  },

  categoryIcon(category) {
    const key = `../assets/categories/category-${category ?? 'other'}.png`;
    return CATEGORY_ICON_URLS[key] ?? CATEGORY_ICON_URLS['../assets/categories/category-other.png'] ?? '';
  },

  locationIcon(location: string | undefined): string {
    if (!location) return '📦';
    // System locations 直接 mapping；自訂 location 用關鍵字推斷，未匹配回 fallback
    const SYSTEM: Record<string, string> = {
      fridge: '🧊',
      freezer: '❄️',
      pantry: '🗄️',
      counter: '🍽️',
      other: '📦',
    };
    if (SYSTEM[location]) return SYSTEM[location];
    // 中文關鍵字推斷
    if (/(冷凍|冰箱.*凍|凍庫)/.test(location)) return '❄️';
    if (/(冰箱|冷藏)/.test(location)) return '🧊';
    if (/(儲藏|櫥櫃|乾貨|架)/.test(location)) return '🗄️';
    if (/(台面|流理|餐桌|廚房)/.test(location)) return '🍽️';
    return '📦';
  },

  rowSummary(row) {
    const first = row.item.batches[0];
    const batchInfo = row.item.batches.length > 1 ? ` · ${row.item.batches.length} 批` : '';
    const expiry = ` · ${this.expiryLabel(row.expiry)}`;
    if (!first) return `0${expiry}`;
    // 套用 preferred display 規則：只有「顆/個」用 base unit，其他都 packaging
    if (first.packageSize && first.packageSize > 0 && first.packageUnit) {
      const u = first.packageUnit.trim();
      if (u === '顆' || u === '個') {
        const totalBase = row.item.batches.reduce(
          (s, b) => s + b.quantity * (b.packageSize ?? 0),
          0,
        );
        return `${this.formatNumberCompact(totalBase)}${u}${batchInfo}${expiry}`;
      }
    }
    return `${this.formatNumberCompact(row.totalQuantity)}${first.unit ?? ''}${batchInfo}${expiry}`;
  },

  consumeStepLabel(unit: string | undefined): string {
    return consumeStep(unit).label;
  },

  /**
   * v0.3.4「用 1 次」label：
   * - 有 packageSize/packageUnit → 用 base unit step（每瓶 936ml → 用 100ml）
   * - 沒 → fallback 到 unit（例：1 盒）
   */
  consumeStepLabelForBatch(batch: Batch): string {
    if (batch.packageUnit) return consumeStep(batch.packageUnit).label;
    return consumeStep(batch.unit).label;
  },

  batchUsagePattern(batch: Batch): 'unit_per_use' | 'continuous' {
    const cat = this.detail.item?.category;
    // v0.3.4 修：batch.unit 是 packaging unit（'瓶'），compareBaseUnit 該用 batch.packageUnit（'ml'）。
    // 缺 packageUnit 時 fallback 用 unit（既有行為）。
    const compareBaseUnit = batch.packageUnit ?? batch.unit;
    return classifyUsagePattern({
      ...(cat ? { category: cat } : {}),
      ...(batch.packageSize !== undefined
        ? { compareBaseUnit, packageSize: batch.packageSize }
        : {}),
    });
  },

  isContinuousAndOpened(batch: Batch): boolean {
    return this.batchUsagePattern(batch) === 'continuous' && batch.openedAt !== null;
  },

  isContinuous(batch: Batch): boolean {
    return this.batchUsagePattern(batch) === 'continuous';
  },

  isContinuousAndUnopened(batch: Batch): boolean {
    return this.batchUsagePattern(batch) === 'continuous' && batch.openedAt === null;
  },

  /** 整 item 是否 continuous（看第一個 batch 推斷；同 item 內 pattern 應一致） */
  itemIsContinuous(): boolean {
    const first = this.detail.batches[0];
    if (!first) return false;
    return this.batchUsagePattern(first) === 'continuous';
  },

  /** detail 內第一個 opened 的 batch（用於 header「用完開瓶這瓶」按鈕對象） */
  firstOpenedBatch(): Batch | undefined {
    return this.detail.batches.find((b) => b.openedAt !== null);
  },

  /** detail 內第一個未開封 batch（用於 header「開瓶第一瓶」按鈕對象） */
  firstUnopenedBatch(): Batch | undefined {
    return this.detail.batches.find((b) => b.openedAt === null);
  },

  /** Continuous item 上方大按鈕：用完開瓶這瓶（扣到 0） */
  async finishOpenedBatch() {
    const item = this.detail.item;
    if (!item) return;
    const opened = this.firstOpenedBatch();
    if (!opened) return;
    try {
      await consumeBatchAmount(item.id, opened.id, opened.quantity);
      await this.refresh();
      await this.reloadDetail();
      this.showToast('開瓶這瓶已用完');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '操作失敗');
    }
  },

  /** Continuous item 上方大按鈕：開第一瓶 */
  async openFirstBatch() {
    const item = this.detail.item;
    if (!item) return;
    const target = this.firstUnopenedBatch();
    if (!target) return;
    try {
      await markBatchOpened(item.id, target.id);
      await this.refresh();
      await this.reloadDetail();
      this.showToast('已開瓶');
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '操作失敗');
    }
  },

  detailHasOnlyContinuousOpened(): boolean {
    if (this.detail.batches.length === 0) return false;
    return this.detail.batches.every((b) => this.isContinuousAndOpened(b));
  },

  async wipeInventoryData() {
    if (!confirm('清除所有庫存與採買資料？\n（保留：設定、知識庫；不可復原）')) return;
    await db.transaction(
      'rw',
      [db.events, db.items, db.shoppingItems, db.subscriptions],
      async () => {
        await db.events.clear();
        await db.items.clear();
        await db.shoppingItems.clear();
        await db.subscriptions.clear();
      },
    );
    this.selectedLocation = null;
    await this.refresh();
    this.showToast('已清除所有庫存與採買資料');
  },

  formatBatchQuantity(batch: Batch): string {
    return this.formatNumberCompact(batch.quantity);
  },

  /**
   * v0.3.5 Item Detail 內依品項屬性顯示「最自然的數量單位」：
   * - packageUnit 是 ml/g/kg/L（容量/重量）→ 顯示 packaging count + unit（4 瓶）
   * - 其他（顆/片/抽/個/包）→ 顯示 base unit total + packageUnit（10 顆）
   * - 沒 packaging 規格 → fallback 直接 quantity + unit
   *
   * 庫存列表（彙總 row）一律 packaging（rowSummary 既有行為），不走此 helper。
   */
  preferredQtyForBatch(batch: Batch): { qty: number; unit: string } {
    if (!batch.packageSize || batch.packageSize <= 0 || !batch.packageUnit) {
      return { qty: batch.quantity, unit: batch.unit };
    }
    const u = batch.packageUnit.trim();
    // 只有「以個體為計量」（顆/個）才用 base unit total（4 顆）；
    // 其他（ml/g/kg/L/抽/片/包/卷/入）都用 packaging count（4 瓶/2 包/3 卷）
    const isItemized = u === '顆' || u === '個';
    if (isItemized) {
      return { qty: batch.quantity * batch.packageSize, unit: u };
    }
    return { qty: batch.quantity, unit: batch.unit };
  },

  formatPreferredBatchQty(batch: Batch): string {
    const p = this.preferredQtyForBatch(batch);
    return `${this.formatNumberCompact(p.qty)}${p.unit}`;
  },

  formatPreferredItemTotal(detail: DetailView): string {
    const first = detail.batches[0];
    if (!first) return '';
    const totalPackaging = detail.batches.reduce((s, b) => s + b.quantity, 0);
    if (!first.packageSize || first.packageSize <= 0 || !first.packageUnit) {
      return `${this.formatNumberCompact(totalPackaging)}${first.unit}`;
    }
    const u = first.packageUnit.trim();
    const isItemized = u === '顆' || u === '個';
    if (isItemized) {
      const totalBase = detail.batches.reduce(
        (s, b) => s + b.quantity * (b.packageSize ?? 0),
        0,
      );
      return `${this.formatNumberCompact(totalBase)}${u}`;
    }
    return `${this.formatNumberCompact(totalPackaging)}${first.unit}`;
  },

  /**
   * v0.3.5：每包裝規格小字，例如「每瓶 936ml」「每盒 10 顆」。
   * 比顯示總量「(內含 3744ml)」直覺多 — 使用者已知 quantity (4 瓶)，規格只是補充。
   */
  formatPerPackagingSpec(batch: Batch): string {
    if (!batch.packageSize || batch.packageSize <= 0 || !batch.packageUnit) return '';
    return `每${batch.unit} ${this.formatNumberCompact(batch.packageSize)}${batch.packageUnit}`;
  },

  formatNumberCompact(n: number): string {
    if (!Number.isFinite(n)) return '0';
    const rounded = Math.round(n * 100) / 100;
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  },

  /**
   * Item 標題列規格小字。v0.3.5 改顯示「每[包裝] N base」（例：「每瓶 936ml」「每盒 10 顆」）
   * 而非總量 — 使用者已從 quantity 看到 4 瓶，規格只是補充。
   */
  totalPackagingLabel(detail: DetailView): string {
    const first = detail.batches[0];
    if (!first || !first.packageSize || first.packageSize <= 0 || !first.packageUnit) {
      return '';
    }
    // 只在所有 batch 同包裝（避免混合 ml + g 之類）
    const allSame = detail.batches.every(
      (b) => b.packageUnit === first.packageUnit && b.unit === first.unit,
    );
    if (!allSame) return '';
    return `每${first.unit} ${this.formatNumberCompact(first.packageSize)}${first.packageUnit}`;
  },

  async consumePackaging(batchId: string) {
    const item = this.detail.item;
    if (!item) return;
    const batch = item.batches.find((b) => b.id === batchId);
    if (!batch) return;
    // v0.3.4：「用 1 整 [包裝]」= 扣 1 個 batch.quantity（packaging count）
    const amount = Math.min(1, batch.quantity);
    try {
      await consumeBatchAmount(item.id, batchId, amount);
      await this.refresh();
      await this.reloadDetail();
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : '消耗失敗');
    }
  },

  formatDate(ms: number | null) {
    if (ms === null) return '—';
    const d = new Date(ms);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  },

  showToast(msg: string) {
    this.toast = msg;
    setTimeout(() => {
      if (this.toast === msg) this.toast = null;
    }, 2400);
  },
}));

function toDateInputValue(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildDetail(item: Item, warningDays: number): DetailView {
  const sortedBatches = [...item.batches]
    .filter((b) => b.quantity > 0)
    .sort((a, b) => {
      if (a.expiresAt === null && b.expiresAt === null) return 0;
      if (a.expiresAt === null) return 1;
      if (b.expiresAt === null) return -1;
      return a.expiresAt - b.expiresAt;
    })
    .map((b) => ({ ...b, expiry: getExpiryStatus(b.expiresAt, warningDays) }));
  const expiry = aggregateItemStatus(item.batches, warningDays);
  const totalQuantity = item.batches.reduce((s, b) => s + b.quantity, 0);
  return { open: true, item, batches: sortedBatches, expiry, totalQuantity };
}

window.addEventListener('mlh:notify', (e) => {
  const detail = (e as CustomEvent).detail as { title: string; body: string };
  console.info('[in-app banner]', detail);
});

Alpine.start();
